import { useSelector } from '@xstate/react';
import classNames from 'classnames';
import type { ActorRefFrom } from 'xstate';
import { LoadingSpinner } from '~/components';
import { LoginImages } from '~/pages/authentication/LoginImages';
import { CVUploadPage, RegistrationCompletePage, TypeCategoryPage, WelcomePage } from './pages';
import { providerRegistrationView } from './registration-view.machine';
import { useSyncClerkName } from './useSyncClerkName';

interface RegistrationProps {
  actorRef: ActorRefFrom<typeof providerRegistrationView>;
}

export function Registration({ actorRef }: RegistrationProps) {
  // [MCV-1762] The sign-up card no longer asks providers for a name, so Clerk has none yet.
  useSyncClerkName(actorRef);

  const viewContext = useSelector(actorRef, (snapshot) => snapshot.context);

  const currentState = useSelector(actorRef, (snapshot) => {
    if (snapshot.matches('$_WELCOME')) return '/registration/welcome';
    if (snapshot.matches('$_TYPE_AND_CATEGORY')) return '/registration/type-category-npi';
    if (snapshot.matches('$_CV_UPLOAD')) return '/registration/cv-upload';
    if (snapshot.matches('$_UPLOADING_CV')) return '/registration/cv-upload';
    if (snapshot.matches('$_PARSING_CV')) return '/registration/cv-upload';
    if (snapshot.matches('$_COMPLETING')) return '/registration/complete';
    return '/registration/loading';
  });

  const renderPage = () => {
    switch (currentState) {
      case '/registration/welcome':
        return <WelcomePage actorRef={actorRef} viewContext={viewContext} />;
      case '/registration/type-category-npi':
        return <TypeCategoryPage actorRef={actorRef} viewContext={viewContext} />;
      case '/registration/cv-upload':
        return <CVUploadPage actorRef={actorRef} viewContext={viewContext} />;
      case '/registration/complete':
        return <RegistrationCompletePage actorRef={actorRef} viewContext={viewContext} />;
      default:
        return <LoadingSpinner size={30} gap={0} speed={300} />;
    }
  };
  return (
    <div className="registration-container">
      <div
        className={classNames(
          'flex flex-col items-center justify-center bg-primary-t-950',
          currentState === '/registration/cv-upload' ? 'min-h-screen py-4' : 'h-screen'
        )}
      >
        <div className="flex flex-col lg:flex-row gap-14">
          <div className="flex flex-col flex-1">
            <div className="w-full max-w-[475px]">{renderPage()}</div>
          </div>
          <div className="hidden lg:flex flex-col items-center shrink-0">
            <LoginImages />
          </div>
        </div>
      </div>
    </div>
  );
}
